/**
 * MOQ Guard — client-side enforcement of variant.custom.moq
 *
 * Why: Shopify B2B catalog quantity rules require Company Location catalogs which
 * this store doesn't have. We use a custom variant metafield `custom.moq` instead.
 * This script intercepts all paths that mutate the cart and auto-bumps qty to MOQ
 * when below, surfacing a toast to the user. Per Shopify Cart Validation Function
 * deployed separately, this is L2 (UI defense). L3 is the function (checkout).
 *
 * Sources of MOQ truth (in priority order):
 *   1. window.__MOQ_MAP__ injected by theme.liquid from current cart line items
 *   2. data-moq attributes on rendered DOM elements (PDP, cards, cart-drawer)
 *   3. Default = 1 (no MOQ) if unknown
 *
 * Lines fall back to MOQ=1 for any variant not seen yet — server-side Shopify
 * Function will reject at checkout in that edge case.
 */
(function () {
  'use strict';

  // Capture pristine fetch reference before any other code can patch it
  const origFetch = window.fetch;

  const MOQ_MAP = (window.__MOQ_MAP__ = window.__MOQ_MAP__ || {});

  function setMOQ(variantId, moq) {
    if (!variantId) return;
    const id = String(variantId);
    const n = parseInt(moq, 10);
    if (n > 0) MOQ_MAP[id] = Math.max(MOQ_MAP[id] || 1, n);
  }

  function getMOQ(variantId) {
    return MOQ_MAP[String(variantId)] || 1;
  }

  /** Line keys from /cart endpoints look like "51604170277185:abc123..." — variant_id is before ":" */
  function variantFromLineKey(key) {
    if (!key) return null;
    const s = String(key);
    const idx = s.indexOf(':');
    return idx > 0 ? s.slice(0, idx) : s;
  }

  /**
   * Cart quantities cache. MOQ semantics follow Shopify's native quantity_rule.min:
   * the MOQ is the minimum TOTAL in cart for that variant, not the minimum per add.
   * We need current cart qty per variant to know whether an add/change would land
   * the cart below or at/above MOQ.
   */
  const CART_QTY_BY_VARIANT = {};

  function getCartQty(variantId) {
    return CART_QTY_BY_VARIANT[String(variantId)] || 0;
  }

  /** Update CART_QTY_BY_VARIANT from a cart JSON object (has .items[]) */
  function applyCartData(data) {
    if (!data || !Array.isArray(data.items)) return false;
    for (const k of Object.keys(CART_QTY_BY_VARIANT)) delete CART_QTY_BY_VARIANT[k];
    data.items.forEach((item) => {
      if (!item || !item.variant_id) return;
      CART_QTY_BY_VARIANT[String(item.variant_id)] =
        (CART_QTY_BY_VARIANT[String(item.variant_id)] || 0) + (item.quantity || 0);
    });
    return true;
  }

  /** Debounced /cart.js fetch to avoid rate-limit spirals */
  let _refreshTimer = null;
  let _refreshInFlight = false;
  let _lastRefreshAt = 0;
  const MIN_REFRESH_INTERVAL_MS = 1500;
  async function refreshCartSnapshot() {
    const now = Date.now();
    const since = now - _lastRefreshAt;
    if (_refreshInFlight) return;
    if (since < MIN_REFRESH_INTERVAL_MS) {
      clearTimeout(_refreshTimer);
      _refreshTimer = setTimeout(refreshCartSnapshot, MIN_REFRESH_INTERVAL_MS - since);
      return;
    }
    _refreshInFlight = true;
    try {
      const res = await origFetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const ct = res.headers.get('content-type') || '';
      if (!ct.includes('json')) return; // HTML body = Cloudflare challenge, skip
      const data = await res.json();
      applyCartData(data);
    } catch (_) {
    } finally {
      _lastRefreshAt = Date.now();
      _refreshInFlight = false;
    }
  }

  /** Scan DOM for data-moq attributes and merge into map */
  function indexFromDOM() {
    document.querySelectorAll('[data-moq]').forEach((el) => {
      const moq = parseInt(el.getAttribute('data-moq'), 10);
      if (!(moq > 0)) return;
      const vid =
        el.getAttribute('data-quantity-variant-id') ||
        el.getAttribute('data-quick-add') ||
        el.getAttribute('data-variant-id') ||
        (el.querySelector && el.querySelector('[data-quantity-variant-id]')?.getAttribute('data-quantity-variant-id'));
      if (vid) setMOQ(vid, moq);
    });
  }

  // Toast UI — minimal, reuses zero existing CSS to avoid coupling
  let toastEl = null;
  let toastTimer = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.id = 'moq-guard-toast';
      toastEl.setAttribute('role', 'alert');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.style.cssText =
        'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);' +
        'background:#000;color:#fff;padding:14px 22px;border-radius:6px;' +
        'z-index:9999;max-width:90vw;box-shadow:0 6px 20px rgba(0,0,0,.25);' +
        'font:14px/1.4 system-ui,sans-serif;display:none;text-align:center;';
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.style.display = 'block';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.style.display = 'none';
    }, 4500);

    // Also publish via theme's pubsub if available so other components can react
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartError, {
          source: 'moq-guard',
          message: msg,
        });
      }
    } catch (_) {}
  }

  function notifyAdjustment(moq) {
    showToast(`Minimum order quantity for this item is ${moq}. Quantity adjusted.`);
  }

  /**
   * Given a variant that would reach `newTotalInCart` after this op, compute the
   * bumped requested qty such that resulting cart qty is exactly MOQ (or original
   * requested qty if it already lands ≥ MOQ). Returns the delta added to `reqQty`
   * (or the absolute new requested qty, depending on caller).
   *
   * Semantics: MOQ applies to TOTAL in cart for the variant, not per transaction.
   * - ADD: total = current_in_cart + reqQty; if total < MOQ → bump reqQty so total = MOQ
   * - CHANGE: reqQty IS the new total; if reqQty > 0 and < MOQ → bump to MOQ; 0 = remove (allowed)
   */

  /** Mutate-in-place enforcement helpers — return true if any change happened */

  function enforceFormDataAdd(formData) {
    const id = formData.get('id');
    const qty = parseInt(formData.get('quantity') || '1', 10);
    if (!id || !(qty >= 0)) return false;
    const moq = getMOQ(id);
    const currentQty = getCartQty(id);
    const wouldBe = currentQty + qty;
    if (wouldBe < moq) {
      const needed = Math.max(moq - currentQty, qty);
      formData.set('quantity', String(needed));
      notifyAdjustment(moq);
      return true;
    }
    return false;
  }

  function enforceJsonAdd(body) {
    let adjusted = false;
    if (body.id != null) {
      const qty = parseInt(body.quantity || 1, 10);
      const moq = getMOQ(body.id);
      const currentQty = getCartQty(body.id);
      const wouldBe = currentQty + qty;
      if (wouldBe < moq) {
        const needed = Math.max(moq - currentQty, qty);
        body.quantity = needed;
        notifyAdjustment(moq);
        adjusted = true;
      }
    }
    if (Array.isArray(body.items)) {
      // Aggregate per-variant requested qty (items[] can repeat same variant)
      const reqPerVariant = {};
      body.items.forEach((it) => {
        const id = String(it.id);
        reqPerVariant[id] = (reqPerVariant[id] || 0) + parseInt(it.quantity || 1, 10);
      });
      body.items.forEach((it, idx) => {
        const id = String(it.id);
        const moq = getMOQ(id);
        const currentQty = getCartQty(id);
        const totalReq = reqPerVariant[id];
        if (currentQty + totalReq < moq) {
          // Bump the first entry for this variant to cover the gap; leave others
          // The simplest robust approach: replace this entry's qty with (MOQ - currentQty)
          // only if this is the first occurrence
          if (idx === body.items.findIndex((x) => String(x.id) === id)) {
            it.quantity = moq - currentQty;
            notifyAdjustment(moq);
            adjusted = true;
          }
        }
      });
    }
    return adjusted;
  }

  function enforceJsonChange(body) {
    // quantity=0 is removal, always allowed
    const rawQty = body.quantity;
    const qty = parseInt(rawQty, 10);
    if (qty === 0) return false;
    const lineKey = body.id || body.line;
    const variantId = variantFromLineKey(lineKey);
    if (!variantId) return false;
    const moq = getMOQ(variantId);
    if (qty > 0 && qty < moq) {
      body.quantity = moq;
      notifyAdjustment(moq);
      return true;
    }
    return false;
  }

  function enforceJsonUpdate(body) {
    if (!body.updates || typeof body.updates !== 'object') return false;
    let adjusted = false;
    Object.keys(body.updates).forEach((key) => {
      const qty = parseInt(body.updates[key], 10);
      if (qty === 0) return; // removal allowed
      const variantId = variantFromLineKey(key) || key;
      const moq = getMOQ(variantId);
      if (qty > 0 && qty < moq) {
        body.updates[key] = moq;
        notifyAdjustment(moq);
        adjusted = true;
      }
    });
    return adjusted;
  }

  function isCartMutationUrl(url) {
    if (!url) return null;
    const u = String(url);
    if (u.includes('/cart/add')) return 'add';
    if (u.includes('/cart/change')) return 'change';
    if (u.includes('/cart/update')) return 'update';
    return null;
  }

  // Patch fetch (origFetch captured at top of IIFE)
  if (origFetch) {
    const patchedFetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        const op = isCartMutationUrl(url);
        if (window.__MOQ_DEBUG__ && op) console.log('[moq-guard] patched fetch enter:', op, url);
        if (op && init && init.body) {
          if (init.body instanceof FormData && op === 'add') {
            enforceFormDataAdd(init.body);
          } else if (typeof init.body === 'string') {
            try {
              const json = JSON.parse(init.body);
              let mutated = false;
              if (op === 'add') mutated = enforceJsonAdd(json);
              else if (op === 'change') mutated = enforceJsonChange(json);
              else if (op === 'update') mutated = enforceJsonUpdate(json);
              if (mutated) init.body = JSON.stringify(json);
            } catch (_) {}
          }
        }
        // After any cart mutation (ours or someone else's), update snapshot so
        // subsequent adds know the updated cart state. We return a proxy
        // Promise that resolves AFTER state update so callers can't race us
        // by chaining a next mutation on the same microtask.
        if (op) {
          if (window.__MOQ_DEBUG__) console.log('[moq-guard] wrapping response for', op);
          return origFetch.apply(this, arguments).then(async (res) => {
            if (window.__MOQ_DEBUG__) console.log('[moq-guard] response arrived', op, res && res.status);
            try {
              if (res && res.ok) {
                const ct = (res.headers && res.headers.get && res.headers.get('content-type')) || '';
                if (ct.includes('json') || ct.includes('javascript')) {
                  const data = await res.clone().json();
                  if (window.__MOQ_DEBUG__) console.log('[moq-guard] response data:', op, data);
                  // /cart/change.js & /cart/update.js return the full cart
                  if (Array.isArray(data.items)) {
                    applyCartData(data);
                    if (window.__MOQ_DEBUG__) console.log('[moq-guard] applyCartData → CART_QTY_BY_VARIANT:', JSON.stringify(CART_QTY_BY_VARIANT));
                  } else if (data && data.variant_id && typeof data.quantity === 'number') {
                    // /cart/add.js returns just the added line; increment map
                    const id = String(data.variant_id);
                    CART_QTY_BY_VARIANT[id] = (CART_QTY_BY_VARIANT[id] || 0) + data.quantity;
                    if (window.__MOQ_DEBUG__) console.log('[moq-guard] add increment → CART_QTY_BY_VARIANT:', JSON.stringify(CART_QTY_BY_VARIANT));
                  } else {
                    // Fallback: debounced /cart.js (rare path)
                    if (window.__MOQ_DEBUG__) console.log('[moq-guard] response had no items[] or variant_id, fallback refresh');
                    refreshCartSnapshot();
                  }
                } else if (window.__MOQ_DEBUG__) {
                  console.log('[moq-guard] skipped response, ct=', ct);
                }
              } else if (window.__MOQ_DEBUG__) {
                console.log('[moq-guard] bad response', res && res.status);
              }
            } catch (e) {
              if (window.__MOQ_DEBUG__) console.warn('[moq-guard] response parse error:', e);
            }
            return res;
          });
        }
      } catch (e) {
        console.warn('[moq-guard] fetch patch error:', e);
      }
      return origFetch.apply(this, arguments);
    };
    patchedFetch.__moqPatched = true;
    window.fetch = patchedFetch;

    // Re-assert ownership periodically to detect/defend against code that
    // replaces window.fetch after us (e.g. a theme/app script that wraps its
    // own fetch). If we detect replacement, we re-patch on top of whatever
    // is there so cart interceptors still fire.
    setInterval(() => {
      if (!window.fetch || !window.fetch.__moqPatched) {
        const priorFetch = window.fetch || origFetch;
        const rePatch = function (input, init) {
          return patchedFetch.call(this, input, init);
        };
        rePatch.__moqPatched = true;
        // Wrap prior fetch: our patchedFetch delegates to origFetch which is
        // the native one — but if someone else patched in between, we need
        // to route through THEIR patch too. The safest is to call priorFetch
        // from within patched flow. For simplicity we accept that our
        // enforcement overrides theirs for cart URLs; non-cart URLs bypass.
        window.fetch = rePatch;
        if (window.__MOQ_DEBUG__) console.warn('[moq-guard] re-patched fetch after external override');
      }
    }, 1000);
  }

  // Patch XMLHttpRequest (some legacy code paths may use it)
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__moq_url = url;
    return origOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function (body) {
    try {
      const op = isCartMutationUrl(this.__moq_url);
      if (op && body) {
        if (body instanceof FormData && op === 'add') {
          enforceFormDataAdd(body);
        } else if (typeof body === 'string') {
          try {
            const json = JSON.parse(body);
            let mutated = false;
            if (op === 'add') mutated = enforceJsonAdd(json);
            else if (op === 'change') mutated = enforceJsonChange(json);
            else if (op === 'update') mutated = enforceJsonUpdate(json);
            if (mutated) {
              return origSend.call(this, JSON.stringify(json));
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      console.warn('[moq-guard] xhr patch error:', e);
    }
    return origSend.apply(this, arguments);
  };

  // Init: index DOM on load + observe for late renders (cart-drawer etc)
  function init() {
    indexFromDOM();
    refreshCartSnapshot();

    const observer = new MutationObserver(() => {
      indexFromDOM();
    });
    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-moq'],
    });

    try {
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
          indexFromDOM();
          refreshCartSnapshot();
        });
      }
    } catch (_) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for diagnostics + tests
  window.__moqGuard = { getMOQ, setMOQ, indexFromDOM, MOQ_MAP, getCartQty, refreshCartSnapshot, CART_QTY_BY_VARIANT };
})();
